import type { CorporateMainNavigationModel } from '@gro-up/api/corporate'
import type { Domain } from '@gro-up/types'
import { computed } from 'vue'
import { useRoute } from '#app'

import { useSiteNavigation } from './use-site-navigation'

export type MainNavTypes = 'corporateSubhomeNavigationLink' | 'sharedLink' | 'sharedButton'

export type FlatNavItem = {
  type?: MainNavTypes
  title: string | undefined
  url: string | undefined
  domain?: Domain
}

function flattenNavItems(items: CorporateMainNavigationModel[] | undefined): FlatNavItem[] {
  return (
    items?.map(navItem => {
      const item = navItem.item
      return {
        type: navItem.type as MainNavTypes | undefined,
        title: item && 'title' in item ? item.title : item && 'name' in item ? item.name : undefined,
        url: item?.url,
        domain: item && 'domain' in item && item.domain ? (item.domain as Domain) : undefined,
      }
    }) ?? []
  )
}

export function useCorporateNavigation() {
  const siteNavigation = useSiteNavigation()
  const route = useRoute()

  const topNav = computed(() => siteNavigation.value?.topNavigation ?? [])
  const mainNav = computed((): FlatNavItem[] => flattenNavItems(siteNavigation.value?.mainNavigation ?? undefined))
  const search = computed(() => siteNavigation.value?.search)
  const domain = computed(() => {
    const d = siteNavigation.value?.domain
    if (!d) return null
    return { ...d, domain: d.domain as Domain | null }
  })
  const subDomain = computed(() => siteNavigation.value?.subDomain ?? null)

  // De aside bevat de sitebrede sprong naar de andere secties. Die is relevant zodra de bezoeker zich in een deel
  // van de site bevindt: een domein (/kinderopvang/) of een subsectie (/groei/). Op losse contentpagina's levert de
  // backend geen van beide en blijft de knop weg.
  const hasAsideNavigation = computed(() => !!domain.value || !!subDomain.value)

  const subHome = computed(() => {
    const subHomeData = siteNavigation.value?.hamburgerNavigation.subHome ?? null
    if (!subHomeData) return null

    // Helper function to check if a URL matches the current route
    const isCurrentPage = (url: string | undefined): boolean => {
      if (!url) return false
      const currentPath = route.path
      // Remove trailing slashes for comparison
      const normalizedUrl = url.replace(/\/$/, '')
      const normalizedCurrentPath = currentPath.replace(/\/$/, '')
      // Only match exact URLs, not prefix matches
      return normalizedUrl === normalizedCurrentPath
    }

    // Update currentPage flags for mainItems
    const mainItems = subHomeData.mainItems?.map(item => ({
      ...item,
      currentPage: isCurrentPage(item.url),
    }))

    // Update currentPage flags for subItems
    const subItems = subHomeData.subItems?.map(item => ({
      ...item,
      currentPage: isCurrentPage(item.url),
    }))

    return {
      ...subHomeData,
      mainItems,
      subItems,
    }
  })
  const primaryLinks = computed((): FlatNavItem[] =>
    flattenNavItems(siteNavigation.value?.hamburgerNavigation.primaryLinks ?? undefined),
  )
  const homeLinks = computed(() => siteNavigation.value?.hamburgerNavigation.homeLinks ?? [])
  const socialMedia = computed(() => siteNavigation.value?.hamburgerNavigation.socialMedia ?? [])

  return {
    topNav,
    mainNav,
    search,
    domain,
    subDomain,
    subHome,
    primaryLinks,
    homeLinks,
    socialMedia,
    hasAsideNavigation,
  }
}

